import {ReactElement, useCallback, useMemo} from 'react';
import {Image, Link} from '@shopify/hydrogen';

import {
  designYourOwnEvent1JPEG,
  designYourOwnEvent2JPEG,
  designYourOwnEvent2WEBP,
} from '../../assets/img';
import VectorIcon from '../VectorIcon';
import {
  EventDetailPageSidebarPromotionTileType,
  EventDetailType,
} from '../../types';
import {useSocialShare} from '../../hooks';
import classNames from 'classnames';
import {
  ExternalEventBuyTicketWidget,
  TwlEventBuyTicketWidget,
} from './BuyTicketWidgets.client';
import {FlattenedProduct} from '../../utils/graphql';
import WidgetArea from '../WidgetArea.client';
import EmbeddedRichTextContent from '../EmbeddedRichTextContent.client';
import {formatEventStartsAtDateTime} from '../../utils';
import EventImagesCarousel from '../carousels/EventImagesCarousel.client';
import EventRecommendedList from './EventRecommendedList.client';
import {getEventBySlug, getEvents} from '../../backendApi';
import {useQuery as useBackendQuery} from 'react-query';
import {useViewKey} from '../../contexts/ViewKeyProvider.client';
import RedirectNonRewardsMember from '../member/RedirectNonRewardsMember.client';
import Skeleton from 'react-loading-skeleton';

interface EventDetailProps {
  event: EventDetailType;
  shopifyProduct?: FlattenedProduct;
}

/*
Event can be one of these:
1. No ticket event: `event.has_tickets` is false
2. Ticket event - External: `event.has_tickets` is true and `event.external_url` is set
3. Ticket event - TWL hosted:
  a. Single session: `event.has_tickets` is true and `event.event_sessions` has only one item
  b. Multiple sessions: `event.has_tickets` is true and `event.event_sessions` has more than one item
*/

export enum EventTicketSource {
  NO_TICKET = 'NO_TICKET',
  EXTERNAL = 'EXTERNAL',
  TWL_TICKET_EVENT = 'TWL_TICKET_EVENT',
}

export default function EventDetail({
  event,
  shopifyProduct = null,
}: EventDetailProps) {
  const viewKey = useViewKey();
  const {
    data: fetchedEventDetail,
    error,
    isLoading,
  } = useBackendQuery(['event', event.slug], () => getEventBySlug(event.slug), {
    enabled: event.is_rewards_members_only,
  });

  const {data: recommendedEvents} = useBackendQuery(
    ['recommendedEvents', event.slug, viewKey],
    () =>
      getEvents(
        {excludeSlug: event.slug, order: '?', upcoming: 'true', pageSize: 3},
        viewKey,
      ),
  );

  // NOTE: we need to sort events by date because we deliberately randomly
  //       ordered the events when fetching them so they are different on
  //       each reload, but we want to show them in date order
  const sortedRecommendedEvents = useMemo(
    () =>
      recommendedEvents?.results &&
      [...recommendedEvents.results].sort(
        (a, b) =>
          new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime(),
      ),
    [recommendedEvents?.results],
  );

  if (!event.is_rewards_members_only) {
    return (
      <_EventDetail
        event={event}
        shopifyProduct={shopifyProduct}
        recommendedEvents={sortedRecommendedEvents}
      />
    );
  }

  if (isLoading) {
    return <EventDetailSkeleton />;
  }
  if (error || !fetchedEventDetail) {
    return <>Error retrieving event</>;
  }

  if (fetchedEventDetail) {
    return (
      <RedirectNonRewardsMember>
        <_EventDetail
          event={fetchedEventDetail}
          shopifyProduct={shopifyProduct}
          recommendedEvents={sortedRecommendedEvents}
        />
      </RedirectNonRewardsMember>
    );
  }
}

function _EventDetail({
  event,
  recommendedEvents,
  shopifyProduct,
}: EventDetailProps & {recommendedEvents: EventDetailType[]}) {
  const {title, event_images, starts_at, has_limited_tickets} = event;

  return (
    <>
      <section>
        <div className="page-title container">
          <h1 className="title-0 page-title__heading">{title}</h1>
          <div className="page-title__meta">
            <div className="page-title__date date-element">
              <i className="icon-date"></i>
              <span>&nbsp;{formatEventStartsAtDateTime(starts_at)}</span>
            </div>
            {has_limited_tickets && (
              <div className="limited-label">
                <i className="icon-tickets"></i>
                &nbsp;<span>Limited tickets available!</span>
              </div>
            )}
          </div>
        </div>
      </section>
      <section>
        <EventImagesCarousel eventImages={event_images} />
      </section>
      <EventBody event={event} shopifyProduct={shopifyProduct} />
      {(recommendedEvents?.length ?? 0) > 0 && (
        <section>
          <EventRecommendedList events={recommendedEvents} />
        </section>
      )}
    </>
  );
}

interface EventBodyProps {
  event: EventDetailType;
  shopifyProduct?: FlattenedProduct;
}

function EventBody({event, shopifyProduct}: EventBodyProps): ReactElement {
  const {
    facebookShareUrl,
    twitterShareUrl,
    linkedinShareUrl,
    emailShareUrl,
    onShare,
  } = useSocialShare(`/events/${event.slug}`, event.title);

  const shareEmail = useCallback(
    (e) => {
      e.preventDefault();
      onShare(emailShareUrl);
    },
    [emailShareUrl, onShare],
  );

  const ticketSource = useMemo((): EventTicketSource => {
    if (event.has_tickets) {
      if (shopifyProduct && shopifyProduct?.variants.length > 0) {
        return EventTicketSource.TWL_TICKET_EVENT;
      } else {
        return EventTicketSource.EXTERNAL;
      }
    }
    return EventTicketSource.NO_TICKET;
  }, [event, shopifyProduct]);

  return (
    <section
      className={classNames('event-detail', {
        'event-detail__no-ticket': ticketSource === EventTicketSource.NO_TICKET,
      })}
    >
      <div className="events has-widget-area container">
        <div className="has-widget-area__l">
          <h2 className="title-1">{event.title}</h2>
          <EmbeddedRichTextContent
            richTextContent={event.content}
            highlights={event.embed_highlights}
          />
          {event.facebook_event_url && (
            <div className="view-event-on-social">
              <Link
                to={event.facebook_event_url}
                className="button button_color_gray button_accent_hover"
                target="_blank"
                rel="noopener noreferrer"
              >
                <VectorIcon iconName="facebook-blue" />
                <span>View event on Facebook</span>
              </Link>
            </div>
          )}
          <div className="share-social-buttons">
            <span>Share this event</span>
            <Link
              to={facebookShareUrl}
              target="_blank"
              prefetch={false}
              className="share-social-buttons__item share-social-buttons__item-f"
            >
              <VectorIcon iconName="facebook-s" />
            </Link>
            <Link
              to={twitterShareUrl}
              target="_blank"
              prefetch={false}
              className="share-social-buttons__item share-social-buttons__item-t"
            >
              <VectorIcon iconName="twitter-s" />
            </Link>
            <Link
              to={linkedinShareUrl}
              target="_blank"
              prefetch={false}
              className="share-social-buttons__item share-social-buttons__item-l"
            >
              <VectorIcon iconName="linkedin-s" />
            </Link>

            <Link
              to={emailShareUrl}
              // NOTE: Firefox behaves odd and opens the mailto link in the
              //       same tab even though we have set target="_blank" so
              //       we use a custom click handler to work around the issue
              onClick={shareEmail}
              prefetch={false}
              target="_blank"
              className="share-social-buttons__item share-social-buttons__item-e"
            >
              <VectorIcon iconName="email-s" />
            </Link>
          </div>
        </div>
        <WidgetArea title="Explore offers &amp; events">
          {ticketSource === EventTicketSource.EXTERNAL && (
            <ExternalEventBuyTicketWidget externalUrl={event.external_url} />
          )}
          {ticketSource === EventTicketSource.TWL_TICKET_EVENT && (
            <TwlEventBuyTicketWidget shopifyProduct={shopifyProduct} />
          )}

          {event.sidebar_promotion && (
            <div className="widgets-mobile-sticky">
              <PromoTile promoTile={event.sidebar_promotion} />
            </div>
          )}
        </WidgetArea>
      </div>
    </section>
  );
}

interface PromoTileProps {
  promoTile: EventDetailPageSidebarPromotionTileType;
}

function PromoTile({promoTile}: PromoTileProps) {
  return (
    <div className="widget-tile red-tile">
      <div className="widget-tile__img">
        <picture>
          <source srcSet={designYourOwnEvent2WEBP} type="image/webp" />
          <source srcSet={designYourOwnEvent2JPEG} type="image/jpeg" />
          <Image
            width={800}
            height={800}
            src={designYourOwnEvent1JPEG}
            alt=""
          />
        </picture>
      </div>
      <div className="widget-tile__content">
        <h2
          className="widget-tile__title"
          dangerouslySetInnerHTML={{__html: promoTile.title}}
        />
        <div className="widget-tile__text-alt">{promoTile.content_text}</div>
        <div className="widget-tile__button">
          <Link
            to={promoTile.button_url}
            className="button button_color_white button_has_icon"
          >
            {promoTile.button_text} <i className="icon-right-arrow1" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function EventDetailSkeleton() {
  return (
    <>
      <section>
        <div className="page-title container mb-0">
          <h1 className="title-0 page-title__heading">
            <Skeleton />
          </h1>
        </div>
      </section>
      <div className="breadcrumbs">
        <Skeleton width="150px" />
      </div>
      <section>
        <div className="hero-slider-outer hero-slider-only-image">
          <div className="hero-slider">
            <div className="hero-slide">
              <Skeleton height="100%" />
            </div>
          </div>
        </div>
      </section>
      <section>
        <div className="news-detail has-widget-area container">
          <div className="has-widget-area__l">
            <div className="title-1">
              <Skeleton />
              <Skeleton width="30%" />
            </div>
            <div className="mt-20">
              <Skeleton width="20%" />
            </div>
            <div className="mt-50">
              <Skeleton count={4} />
              <Skeleton width="60%" />
            </div>
            <div className="mt-50">
              <Skeleton count={6} />
              <Skeleton width="80%" />
            </div>
            <div className="mt-50">
              <Skeleton count={5} />
              <Skeleton width="30%" />
            </div>
          </div>
          <WidgetArea title="Explore offers &amp; events">
            <div className="widgets-mobile-sticky" id="mobileWidgetsSticky">
              <Skeleton width="100%" height={400} />
            </div>
          </WidgetArea>
        </div>
      </section>
    </>
  );
}
